import { useEffect, useMemo, useState } from 'react';

import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Group,
  Loader,
  Modal,
  MultiSelect,
  ScrollArea,
  Select,
  SimpleGrid,
  Stack,
  TagsInput,
  Text,
  Textarea,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconArrowsSplit, IconRefresh, IconTrash } from '@tabler/icons-react';

import { AddCard } from '@/components/AddCard';
import { jurisdictionFieldLabel, JURISDICTIONS, SUBJECT_TYPE_LABELS } from '@/constants';
import { useReportRequestTriage } from '@/hooks';
import { vnextApiClient } from '@/services/vnext-api';
import { TriageAddSubjectRequest } from '@/types/report-request';
import { ApiSubject } from '@/types/screening';

interface TriageSubjectsPanelProps {
  reportRequestId: string;
  /** True when the request is past SUBJECTS_EXTRACTED — the backend
   * rejects edits then, so the mutating affordances are hidden. */
  readOnly?: boolean;
  /** Splits this request into one sibling per subject — the extracted
   * Subject rows move over intact (no re-extraction). */
  onSplit?: () => Promise<void>;
  /** Subject ids flagged by the gate blockers, mapped to what each is
   * missing (e.g. 'jurisdiction', 'subject type') — the matching cards
   * highlight so the analyst can see which subject blocks Continue. */
  attention?: Record<string, string[]>;
  triageActions: Pick<
    ReturnType<typeof useReportRequestTriage>,
    'addSubject' | 'editSubject' | 'removeSubject' | 'reExtractSubject'
  >;
}

/**
 * Card grid for the Subjects workspace section (SUBJECTS_EXTRACTED).
 *
 * Per Subject: shows the structured fields the extraction graph
 * produced (subject_name, subject_type, jurisdictions, the keys in
 * additional_info), plus the source string. Inline edit, mark-for-
 * removal, and "re-extract this subject" actions. The grid ends with a
 * dimmed "+" card for the name the client forgot (or extraction missed)
 * — typed in by hand, no revert to Define needed. The gate strip owns
 * the Continue — this body has no advance affordance.
 */
export function TriageSubjectsPanel({
  reportRequestId,
  readOnly = false,
  onSplit,
  attention,
  triageActions,
}: Readonly<TriageSubjectsPanelProps>) {
  const [subjects, setSubjects] = useState<ApiSubject[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [splitConfirmOpen, setSplitConfirmOpen] = useState(false);
  const [splitting, setSplitting] = useState(false);
  const [addOpen, { open: openAdd, close: closeAdd }] = useDisclosure(false);

  const refetch = useMemo(
    () => async () => {
      if (!reportRequestId) {
        return;
      }
      setIsLoading(true);
      setError(null);
      try {
        const rows = await vnextApiClient.listTriageSubjects(reportRequestId);
        setSubjects(rows);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to load subjects');
      } finally {
        setIsLoading(false);
      }
    },
    [reportRequestId]
  );

  useEffect(() => {
    void refetch();
  }, [refetch]);

  const canSplit = !readOnly && !!onSplit && (subjects?.length ?? 0) > 1;
  const handleSplit = async () => {
    if (!onSplit) {
      return;
    }
    setSplitting(true);
    try {
      await onSplit();
      setSplitConfirmOpen(false);
      await refetch();
    } catch {
      // Errors are toasted by the triage hooks; keep the modal open.
    } finally {
      setSplitting(false);
    }
  };

  return (
    <Stack gap="sm">
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Text size="sm" c="dimmed">
          {readOnly
            ? 'The subjects extracted for this request. Use “Change” on this section to revert and edit them.'
            : 'Review the structured data extracted from each subject string. Edit inline, remove duplicates, or re-extract if you tweaked the source.'}
        </Text>
        {canSplit && (
          <Button
            variant="subtle"
            size="compact-xs"
            leftSection={<IconArrowsSplit size={14} />}
            onClick={() => setSplitConfirmOpen(true)}
            style={{ flexShrink: 0 }}
            data-testid="subjects-split-request"
          >
            Split into {subjects?.length} separate requests
          </Button>
        )}
      </Group>

      {!readOnly && (
        <AddSubjectModal
          opened={addOpen}
          onClose={closeAdd}
          onSubmit={async (body) => {
            await triageActions.addSubject(body);
            await refetch();
          }}
        />
      )}

      {isLoading && !subjects && <Loader />}
      {error && (
        <Text c="red" size="sm">
          {error}
        </Text>
      )}

      {subjects && subjects.length === 0 && (
        <Text size="sm" c="dimmed">
          No subjects extracted yet.{' '}
          {readOnly
            ? 'Change the Define section to edit the source strings, then continue again.'
            : 'Add one by hand, or change the Define section to edit the source strings and continue again.'}
        </Text>
      )}

      <Modal
        opened={splitConfirmOpen}
        onClose={() => setSplitConfirmOpen(false)}
        title="Split into separate requests?"
      >
        <Stack gap="sm">
          <Text size="sm">
            Each subject becomes its own report request with the same team, config, pricing and
            attachments — the extracted details carry over without re-running extraction. This
            request keeps the first subject.
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setSplitConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              loading={splitting}
              onClick={() => void handleSplit()}
              data-testid="confirm-split"
            >
              Split
            </Button>
          </Group>
        </Stack>
      </Modal>

      {subjects && (subjects.length > 0 || !readOnly) && (
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
          {subjects.map((s) => (
            <SubjectCard
              key={s.id}
              subject={s}
              needsAttention={attention?.[s.id] ?? []}
              readOnly={readOnly}
              onEdit={async (body) => {
                await triageActions.editSubject(s.id, body);
                await refetch();
              }}
              onRemove={async () => {
                await triageActions.removeSubject(s.id);
                await refetch();
              }}
              onReExtract={async (additionalContext) => {
                await triageActions.reExtractSubject(s.id, additionalContext);
                await refetch();
              }}
              canRemove={subjects.length > 1}
            />
          ))}
          {!readOnly && (
            <AddCard
              label="Add subject"
              hint="One the request didn't name, or extraction missed"
              ariaLabel="Add a subject"
              onClick={openAdd}
              data-testid="subjects-add"
            />
          )}
        </SimpleGrid>
      )}
    </Stack>
  );
}

interface SubjectCardProps {
  subject: ApiSubject;
  /** What this subject is missing before triage can continue (from the
   * gate blockers) — non-empty renders the card in its warning state. */
  needsAttention?: string[];
  readOnly?: boolean;
  onEdit: (body: {
    subject_name?: string;
    subject_type?: string | null;
    reference?: string | null;
    additional_info?: Record<string, unknown> | null;
    jurisdiction_codes?: string[];
  }) => Promise<void>;
  onRemove: () => Promise<void>;
  onReExtract: (additionalContext?: string) => Promise<void>;
  canRemove: boolean;
}

function SubjectCard({
  subject,
  needsAttention = [],
  readOnly = false,
  onEdit,
  onRemove,
  onReExtract,
  canRemove,
}: Readonly<SubjectCardProps>) {
  const [editOpen, { open: openEdit, close: closeEdit }] = useDisclosure(false);
  const [confirmRemoveOpen, { open: openRemove, close: closeRemove }] = useDisclosure(false);
  const [reExtracting, setReExtracting] = useState(false);

  const additionalInfo = subject.additional_info ?? {};
  const sourceString = (additionalInfo.raw_query as string | undefined) ?? null;

  const handleReExtract = async () => {
    setReExtracting(true);
    try {
      await onReExtract();
    } finally {
      setReExtracting(false);
    }
  };

  const flagged = needsAttention.length > 0;

  return (
    <Card
      shadow="xs"
      padding="md"
      radius="md"
      withBorder
      style={flagged ? { borderColor: 'var(--mantine-color-red-6)' } : undefined}
      data-testid={flagged ? 'subject-card-needs-attention' : 'subject-card'}
    >
      <Stack gap="xs">
        <Group justify="space-between" align="flex-start">
          <Stack gap={2}>
            <Text fw={600} size="md">
              {subject.subject_name}
            </Text>
            <Group gap={4}>
              {subject.subject_type && (
                <Badge color="gray" variant="light">
                  {subject.subject_type}
                </Badge>
              )}
              {needsAttention.map((need) => (
                <Badge key={need} color="red" variant="light">
                  Missing {need}
                </Badge>
              ))}
            </Group>
          </Stack>
          {!readOnly && (
            <Group gap="xs">
              <Tooltip label="Re-extract from the source string">
                <ActionIcon
                  variant="subtle"
                  onClick={handleReExtract}
                  loading={reExtracting}
                  aria-label="Re-extract"
                >
                  <IconRefresh size={16} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label={canRemove ? 'Remove this subject' : 'Cannot remove the last subject'}>
                <ActionIcon
                  variant="subtle"
                  color="red"
                  onClick={openRemove}
                  disabled={!canRemove}
                  aria-label="Remove"
                >
                  <IconTrash size={16} />
                </ActionIcon>
              </Tooltip>
            </Group>
          )}
        </Group>

        {sourceString && (
          <Text size="xs" c="dimmed">
            Source: <em>"{sourceString}"</em>
          </Text>
        )}

        {(subject.jurisdictions ?? []).length > 0 && (
          <Group gap={4} wrap="wrap">
            {(subject.jurisdictions ?? []).map((j) => (
              <Badge key={j.alpha2code} variant="dot" size="sm">
                {j.name ?? j.alpha2code}
              </Badge>
            ))}
          </Group>
        )}

        {Object.keys(additionalInfo).length > 0 && (
          <Stack gap={2}>
            {Object.entries(additionalInfo)
              .filter(([k]) => k !== 'raw_query' && k !== 'uncategorised')
              .map(([k, v]) => (
                <Text key={k} size="xs">
                  <Text component="span" c="dimmed">
                    {k}:
                  </Text>{' '}
                  {Array.isArray(v) ? v.join(', ') : String(v)}
                </Text>
              ))}
          </Stack>
        )}

        {!readOnly && (
          <Button variant="subtle" size="xs" onClick={openEdit}>
            Edit details
          </Button>
        )}
      </Stack>

      <SubjectEditModal
        subject={subject}
        opened={editOpen}
        onClose={closeEdit}
        onSubmit={onEdit}
        onReExtractContext={(context) => onReExtract(context)}
      />

      <Modal opened={confirmRemoveOpen} onClose={closeRemove} title="Remove this subject?">
        <Stack gap="sm">
          <Text size="sm">
            Removes <strong>{subject.subject_name}</strong> from the report. The Subject row and any
            pending identification candidates are deleted.
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={closeRemove}>
              Cancel
            </Button>
            <Button
              color="red"
              onClick={async () => {
                await onRemove();
                closeRemove();
              }}
            >
              Remove
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Card>
  );
}

// The flat extraction-shaped additional_info keys the modal edits.
// Anything else in the dict (raw_query, uncategorised, unknown keys) is
// preserved untouched on save.
const INFO_SCALAR_FIELDS = [
  ['date_of_birth', 'Date of birth'],
  ['registration_number', 'Registration number'],
  ['imo_number', 'IMO number'],
  ['nationality', 'Nationality'],
  ['employer', 'Employer'],
  ['spouse', 'Spouse'],
] as const;

const INFO_ARRAY_FIELDS = [
  ['aliases', 'Aliases'],
  ['addresses', 'Addresses'],
  ['urls', 'URLs'],
  ['previous_employers', 'Previous employers'],
  ['previous_locations', 'Previous locations'],
  ['family_members', 'Family members'],
] as const;

interface SubjectInfoFieldsProps {
  scalars: Record<string, string>;
  onScalarChange: (key: string, value: string) => void;
  arrays: Record<string, string[]>;
  onArrayChange: (key: string, value: string[]) => void;
  /** `subject-info` on edit, `add-subject-info` on add — the tests key on it. */
  testIdPrefix: string;
}

/** The flat additional_info fields both subject modals edit: the scalar
 * grid plus the multi-value tag inputs. */
function SubjectInfoFields({
  scalars,
  onScalarChange,
  arrays,
  onArrayChange,
  testIdPrefix,
}: Readonly<SubjectInfoFieldsProps>) {
  return (
    <>
      <SimpleGrid cols={2} spacing="sm">
        {INFO_SCALAR_FIELDS.map(([key, label]) => (
          <TextInput
            key={key}
            label={label}
            value={scalars[key] ?? ''}
            onChange={(e) => onScalarChange(key, e.currentTarget.value)}
            data-testid={`${testIdPrefix}-${key}`}
          />
        ))}
      </SimpleGrid>
      {INFO_ARRAY_FIELDS.map(([key, label]) => (
        <TagsInput
          key={key}
          label={label}
          value={arrays[key] ?? []}
          onChange={(v) => onArrayChange(key, v)}
          splitChars={['\n']}
          data-testid={`${testIdPrefix}-${key}`}
        />
      ))}
    </>
  );
}

function seedScalars(info: Record<string, unknown>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key] of INFO_SCALAR_FIELDS) {
    const v = info[key];
    out[key] = v === null || v === undefined ? '' : String(v);
  }
  return out;
}

function seedArrays(info: Record<string, unknown>): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  for (const [key] of INFO_ARRAY_FIELDS) {
    const v = info[key];
    out[key] = Array.isArray(v) ? v.map(String) : [];
  }
  return out;
}

interface SubjectEditModalProps {
  subject: ApiSubject;
  opened: boolean;
  onClose: () => void;
  onSubmit: (body: {
    subject_name?: string;
    subject_type?: string | null;
    reference?: string | null;
    additional_info?: Record<string, unknown> | null;
    jurisdiction_codes?: string[];
  }) => Promise<void>;
  /** Re-runs extraction with the analyst's context after the save. */
  onReExtractContext: (context: string) => Promise<void>;
}

function SubjectEditModal({
  subject,
  opened,
  onClose,
  onSubmit,
  onReExtractContext,
}: Readonly<SubjectEditModalProps>) {
  const [name, setName] = useState(subject.subject_name);
  const [subjectType, setSubjectType] = useState<string>(subject.subject_type ?? '');
  const [reference, setReference] = useState(subject.reference ?? '');
  const [jurisdictionCodes, setJurisdictionCodes] = useState<string[]>(
    (subject.jurisdictions ?? []).map((j) => j.alpha2code)
  );
  const [scalars, setScalars] = useState<Record<string, string>>(() =>
    seedScalars(subject.additional_info ?? {})
  );
  const [arrays, setArrays] = useState<Record<string, string[]>>(() =>
    seedArrays(subject.additional_info ?? {})
  );
  const [additionalContext, setAdditionalContext] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setName(subject.subject_name);
    setSubjectType(subject.subject_type ?? '');
    setReference(subject.reference ?? '');
    setJurisdictionCodes((subject.jurisdictions ?? []).map((j) => j.alpha2code));
    setScalars(seedScalars(subject.additional_info ?? {}));
    setArrays(seedArrays(subject.additional_info ?? {}));
    setAdditionalContext('');
  }, [subject]);

  const buildAdditionalInfo = (): Record<string, unknown> | null => {
    // Start from the stored dict so raw_query/uncategorised/unknown keys
    // survive; only the edited keys are overwritten (or dropped when
    // emptied).
    const merged: Record<string, unknown> = { ...(subject.additional_info ?? {}) };
    for (const [key] of INFO_SCALAR_FIELDS) {
      const v = (scalars[key] ?? '').trim();
      if (v) {
        merged[key] = v;
      } else {
        delete merged[key];
      }
    }
    for (const [key] of INFO_ARRAY_FIELDS) {
      const v = (arrays[key] ?? []).map((s) => s.trim()).filter(Boolean);
      if (v.length > 0) {
        merged[key] = v;
      } else {
        delete merged[key];
      }
    }
    return Object.keys(merged).length > 0 ? merged : null;
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await onSubmit({
        subject_name: name,
        subject_type: subjectType || null,
        reference: reference || null,
        additional_info: buildAdditionalInfo(),
        jurisdiction_codes: jurisdictionCodes,
      });
      const context = additionalContext.trim();
      if (context) {
        await onReExtractContext(context);
      }
      onClose();
    } catch {
      // Errors are toasted by the triage hooks — keep the modal open so
      // the analyst's input survives.
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title={`Edit ${subject.subject_name}`} size="lg">
      {/* Fields scroll; the footer stays anchored so Save is always
          reachable (jurisdictions are required to advance — the control
          must never hide below the fold). */}
      <Stack gap="sm">
        <ScrollArea.Autosize mah="60vh" type="auto" offsetScrollbars>
          <Stack gap="sm">
            <TextInput
              label="Subject name"
              value={name}
              onChange={(e) => setName(e.currentTarget.value)}
              required
            />
            <Group grow>
              <Select
                label="Subject type"
                data={[...SUBJECT_TYPE_LABELS]}
                value={subjectType || null}
                onChange={(v) => setSubjectType(v ?? '')}
                clearable
                data-testid="subject-type-select"
              />
              <TextInput
                label="Reference"
                value={reference}
                onChange={(e) => setReference(e.currentTarget.value)}
              />
            </Group>
            <MultiSelect
              label={jurisdictionFieldLabel(subjectType)}
              searchable
              data={JURISDICTIONS.map((j) => ({ value: j.alpha2code, label: j.name }))}
              value={jurisdictionCodes}
              onChange={setJurisdictionCodes}
            />

            <SubjectInfoFields
              scalars={scalars}
              onScalarChange={(key, v) => setScalars((prev) => ({ ...prev, [key]: v }))}
              arrays={arrays}
              onArrayChange={(key, v) => setArrays((prev) => ({ ...prev, [key]: v }))}
              testIdPrefix="subject-info"
            />

            <Textarea
              label="Additional context"
              description="Re-runs extraction with this context after saving — may overwrite the manual edits above."
              placeholder="e.g. The Acme Holdings chairman, not the footballer"
              value={additionalContext}
              onChange={(e) => setAdditionalContext(e.currentTarget.value)}
              minRows={2}
              autosize
              data-testid="subject-additional-context"
            />
          </Stack>
        </ScrollArea.Autosize>

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={submitting} disabled={!name.trim()}>
            {additionalContext.trim() ? 'Save & re-extract' : 'Save'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

interface AddSubjectModalProps {
  opened: boolean;
  onClose: () => void;
  onSubmit: (body: TriageAddSubjectRequest) => Promise<void>;
}

/**
 * "Add subject" — the same structured fields the edit modal offers,
 * starting blank. The name is the only required field; jurisdictions
 * and type are what the gate will ask for next, so they are here too.
 */
function AddSubjectModal({ opened, onClose, onSubmit }: Readonly<AddSubjectModalProps>) {
  const [name, setName] = useState('');
  const [subjectType, setSubjectType] = useState<string>('');
  const [reference, setReference] = useState('');
  const [jurisdictionCodes, setJurisdictionCodes] = useState<string[]>([]);
  const [scalars, setScalars] = useState<Record<string, string>>(() => seedScalars({}));
  const [arrays, setArrays] = useState<Record<string, string[]>>(() => seedArrays({}));
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setName('');
    setSubjectType('');
    setReference('');
    setJurisdictionCodes([]);
    setScalars(seedScalars({}));
    setArrays(seedArrays({}));
  };

  const buildAdditionalInfo = (): Record<string, unknown> | null => {
    const info: Record<string, unknown> = {};
    for (const [key] of INFO_SCALAR_FIELDS) {
      const v = (scalars[key] ?? '').trim();
      if (v) {
        info[key] = v;
      }
    }
    for (const [key] of INFO_ARRAY_FIELDS) {
      const v = (arrays[key] ?? []).map((s) => s.trim()).filter(Boolean);
      if (v.length > 0) {
        info[key] = v;
      }
    }
    return Object.keys(info).length > 0 ? info : null;
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await onSubmit({
        subject_name: name.trim(),
        subject_type: subjectType || null,
        reference: reference.trim() || null,
        additional_info: buildAdditionalInfo(),
        jurisdiction_codes: jurisdictionCodes,
      });
      reset();
      onClose();
    } catch {
      // Errors are toasted by the triage hooks — keep the modal open so
      // the analyst's input survives.
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title="Add a subject" size="lg">
      <Stack gap="sm">
        <Text size="sm" c="dimmed">
          For a subject the request didn't name (or extraction missed). It joins this request's
          subjects and its source strings, so it survives a revert and re-run.
        </Text>
        <ScrollArea.Autosize mah="60vh" type="auto" offsetScrollbars>
          <Stack gap="sm">
            <TextInput
              label="Subject name"
              value={name}
              onChange={(e) => setName(e.currentTarget.value)}
              required
              data-autofocus
              data-testid="add-subject-name"
            />
            <Group grow>
              <Select
                label="Subject type"
                data={[...SUBJECT_TYPE_LABELS]}
                value={subjectType || null}
                onChange={(v) => setSubjectType(v ?? '')}
                clearable
                data-testid="add-subject-type-select"
              />
              <TextInput
                label="Reference"
                value={reference}
                onChange={(e) => setReference(e.currentTarget.value)}
                data-testid="add-subject-reference"
              />
            </Group>
            <MultiSelect
              label={jurisdictionFieldLabel(subjectType)}
              searchable
              data={JURISDICTIONS.map((j) => ({ value: j.alpha2code, label: j.name }))}
              value={jurisdictionCodes}
              onChange={setJurisdictionCodes}
              data-testid="add-subject-jurisdictions"
            />

            <SubjectInfoFields
              scalars={scalars}
              onScalarChange={(key, v) => setScalars((prev) => ({ ...prev, [key]: v }))}
              arrays={arrays}
              onArrayChange={(key, v) => setArrays((prev) => ({ ...prev, [key]: v }))}
              testIdPrefix="add-subject-info"
            />
          </Stack>
        </ScrollArea.Autosize>

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            loading={submitting}
            disabled={!name.trim()}
            data-testid="add-subject-submit"
          >
            Add subject
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
